import { z } from 'zod';

/**
 * The OLT page's interactive console: a live telnet/SSH session to the
 * chassis, carried over a WebSocket at `/ws/olts/:id/console`.
 *
 * The API logs in with the OLT's stored credentials — they never reach the
 * browser — and from then on keystrokes go straight to the chassis and its
 * output comes straight back, the way PuTTY would show it. Two things sit in
 * the middle: a line that could drop the chassis waits at its Enter for the
 * OLT's name, and every command line is written to the action log.
 */

/** A session with no keystrokes for this long is closed. */
export const OLT_CONSOLE_IDLE_MS = 15 * 60_000;
/** No session outlives this, however busy. */
export const OLT_CONSOLE_MAX_MS = 2 * 60 * 60_000;
/**
 * Live console sessions per OLT, across every operator. Each one is a login
 * line on the chassis, and the platform's own sweeps and ONU pages need theirs.
 */
export const OLT_CONSOLE_PER_OLT = 2;

/**
 * Why a session ended.
 *
 * `chassis_idle` is the chassis's OWN idle timeout, not ours: both OLTs on the
 * platform carry `line telnet idle-timeout 1` and log a telnet login out ~60 s
 * after its last keystroke (measured 2026-09-25: 64 s on gachie1, 61 s on
 * lagos). No telnet no-op resets that timer — NOP is ignored and AYT only makes
 * the chassis print a joke timestamp — so the console says what happened and
 * logs in again on the operator's next keystroke. `chassis` is the chassis
 * ending it for another reason: an `exit` at the top, a reload, the network.
 */
export const OLT_CONSOLE_CLOSE_CODES = [
  'browser',
  'idle',
  'lifetime',
  'chassis',
  'chassis_idle',
  'shutdown',
] as const;
export type OltConsoleCloseCode = (typeof OLT_CONSOLE_CLOSE_CODES)[number];

/** A chassis that ends a session at least this long after the last keystroke timed it out. */
export const OLT_CONSOLE_CHASSIS_IDLE_MS = 50_000;

export const OLT_CONSOLE_COLS = { min: 20, max: 500, default: 120 } as const;
export const OLT_CONSOLE_ROWS = { min: 5, max: 200, default: 32 } as const;

export const oltConsoleClientMessageSchema = z.discriminatedUnion('type', [
  /** Keystrokes, exactly as the terminal produced them. */
  z.object({ type: z.literal('input'), data: z.string().max(64 * 1024) }),
  z.object({
    type: z.literal('resize'),
    cols: z.number().int().min(OLT_CONSOLE_COLS.min).max(OLT_CONSOLE_COLS.max),
    rows: z.number().int().min(OLT_CONSOLE_ROWS.min).max(OLT_CONSOLE_ROWS.max),
  }),
  /** Releases a held line: `name` must be the OLT's name, exactly. */
  z.object({ type: z.literal('confirm'), name: z.string().max(200) }),
  /** Clears a held line from the chassis's prompt without running it. */
  z.object({ type: z.literal('cancel') }),
  z.object({ type: z.literal('ping') }),
]);
export type OltConsoleClientMessage = z.infer<typeof oltConsoleClientMessageSchema>;

export type OltConsoleServerMessage =
  /** Logged in; `output` follows. */
  | { type: 'ready'; olt: string; transport: 'ssh' | 'telnet'; idleMinutes: number }
  /** Chassis output, verbatim — escape sequences and all. */
  | { type: 'output'; data: string }
  /** A line that could drop the chassis is waiting at its Enter. */
  | { type: 'confirm'; command: string }
  /** Something the platform says, not the chassis — shown apart from its output. */
  | { type: 'notice'; message: string }
  /** The session could not open, or was refused. The socket closes after it. */
  | { type: 'error'; message: string }
  /** The session ended; `reason` says why in words, `code` for the page. The socket closes after it. */
  | { type: 'closed'; code: OltConsoleCloseCode; reason: string }
  | { type: 'pong' };
